// 月途中入退社の日割り計算（本城さん 2026-07-16 確定回答・Issue #49）
//   - 対象は3項目のみ: アプリ利用料 / 車両リース料 / 携帯リース料（ガソリン・指導料は対象外）
//   - 日割り額 = 月額 ÷ その月の日数 × 在籍日数、端数は切り上げ（Math.ceil）
//   - 在籍日数は両端含む（例: 6/4入社・6月30日まで在籍 → 6/4〜6/30 = 27日）
//   - 月途中退職も同じ式で対称に日割りする（三浦さん判断。本城さんへは別途確認事項として伝える）
//
// 日付はすべて "YYYY-MM-DD" 文字列のまま比較・演算する（辞書順=時系列順のため文字列比較で足り、
// Date化によるタイムゾーン事故を避けられる）。

// salesDate（対象月の月末日 "YYYY-MM-DD"）から対象月・在籍日数を算出する。
//   days: 対象月内の在籍日数（両端含む）。対象月にまったく在籍がなければ 0
//   daysInMonth: 対象月の日数（salesDate の日部分）
//   prorated: 通月在籍でない（＝日割りが必要）かどうか。
//             true = contractFrom が月初より後、または contractTo が月末より前
export function daysInService(
  salesDate: string,
  contractFrom: string | null,
  contractTo: string | null
): { days: number; daysInMonth: number; prorated: boolean } {
  const targetMonth = salesDate.slice(0, 7); // "YYYY-MM"
  const monthStart = `${targetMonth}-01`;
  const daysInMonth = Number(salesDate.slice(8, 10));

  // 在籍区間の開始 = max(contractFrom, 月初)、終了 = min(contractTo, 月末)
  const start = contractFrom && contractFrom > monthStart ? contractFrom : monthStart;
  const end = contractTo && contractTo < salesDate ? contractTo : salesDate;

  if (start > end) {
    // 対象月にまったく在籍していない（例: contractTo が前月末以前 / contractFrom が翌月以降）
    return { days: 0, daysInMonth, prorated: false };
  }

  // start・end は月初〜月末の範囲に収まることが保証されているため（max/minでクランプ済み）、
  // 日部分だけの引き算で在籍日数が求まる（両端含む=+1）。
  const startDay = Number(start.slice(8, 10));
  const endDay = Number(end.slice(8, 10));
  const days = endDay - startDay + 1;

  const prorated = start !== monthStart || end !== salesDate;

  return { days, daysInMonth, prorated };
}

// 日割り額（端数切り上げ）。prorated=false（通月在籍）なら月額をそのまま返す。
// 対象月にまったく在籍していない（days<=0）場合は 0 を返す（呼び出し側で控除行自体を出さない判断に使う）。
export function prorateMonthlyAmount(
  amount: number,
  salesDate: string,
  contractFrom: string | null,
  contractTo: string | null
): number {
  const { days, daysInMonth, prorated } = daysInService(salesDate, contractFrom, contractTo);
  if (days <= 0) return 0; // 対象月にまったく在籍していない（daysInServiceはこの場合prorated=falseを返すため先に判定する）
  if (!prorated) return amount;
  // amount × 在籍日数 ÷ 月の日数 の順で1回の除算にまとめ、浮動小数の誤差経路を減らす
  // （÷daysInMonth→×days の2段階でも数学的には同値だが、除算回数は少ない方が安全）
  return Math.ceil((amount * days) / daysInMonth);
}

// 日割り適用行に付ける計算根拠のnote。ICB担当者が検算できるよう、対象日数と入社/退職日を明記する。
// 通月在籍（日割りなし）なら null を返す。
export function buildProrateNote(
  salesDate: string,
  contractFrom: string | null,
  contractTo: string | null
): string | null {
  const { days, daysInMonth, prorated } = daysInService(salesDate, contractFrom, contractTo);
  if (!prorated) return null;

  const monthStart = `${salesDate.slice(0, 7)}-01`;
  const reasons: string[] = [];
  if (contractFrom && contractFrom > monthStart) reasons.push(`${contractFrom} 入社`);
  if (contractTo && contractTo < salesDate) reasons.push(`${contractTo} 契約終了`);

  return `日割り ${days}/${daysInMonth}日（${reasons.join(" / ")}）`;
}
